import React, { useState, useEffect } from "react";
import {
  Stack,
  Paper,
  TextInput,
  Textarea,
  Select,
  Switch,
  Button,
  Group,
  Text,
  Skeleton,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifyError, notifySuccess } from "../../utils/notify";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useVoiceAI, VoiceAISettings } from "../../context/VoiceAIContext";

const VOICE_OPTIONS = [
  { value: "Kylie", label: "Kylie (Female)" },
  { value: "Elliot", label: "Elliot (Male)" },
  { value: "Sarah", label: "Sarah (Female)" },
  { value: "Mark", label: "Mark (Male)" },
  { value: "Jessica", label: "Jessica (Female)" },
];

export default function SettingsTab() {
  const { settings, settingsLoading, updateSettings } = useVoiceAI();
  const [saving, setSaving] = useState(false);

  const form = useForm<Partial<VoiceAISettings>>({
    initialValues: {
      greeting: "",
      transfer_number: "",
      system_prompt_instructions: "",
      voice_id: "Kylie",
      is_enabled: true,
      use_business_hours_forwarding: false,
    },
  });

  useEffect(() => {
    if (settings) {
      form.setValues({
        greeting: settings.greeting || "",
        transfer_number: settings.transfer_number || "",
        system_prompt_instructions: settings.system_prompt_instructions || "",
        voice_id: settings.voice_id || "Kylie",
        is_enabled: settings.is_enabled ?? true,
        use_business_hours_forwarding:
          settings.use_business_hours_forwarding ?? false,
      });
    }
    // `form` intentionally omitted: we only want to (re)hydrate the form when
    // the server settings object itself changes, not on every keystroke. Adding
    // `form` to the deps would loop because form.setValues triggers a re-render.
    // eslint-disable-next-line
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(form.values);
      notifySuccess("Voice AI settings have been updated", {
        title: "Settings saved",
      });
    } catch (error: any) {
      notifyError(error.message || "Please try again", {
        title: "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  if (settingsLoading) {
    return (
      <Stack gap="sm">
        <Skeleton height={60} />
        <Skeleton height={120} />
        <Skeleton height={60} />
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Text fw={600}>Voice AI Status</Text>
            <Text size="sm" c="dimmed">
              Enable or disable the AI receptionist for incoming calls
            </Text>
          </div>
          <Switch
            size="lg"
            checked={form.values.is_enabled}
            onChange={(e) =>
              form.setFieldValue("is_enabled", e.currentTarget.checked)
            }
          />
        </Group>
      </Paper>

      <Paper p="md" withBorder>
        <Text fw={600} mb="md">
          Greeting & Voice
        </Text>
        <Stack gap="sm">
          <Textarea
            label="Greeting Message"
            description="The first thing the AI says when answering a call"
            placeholder="Hello! Thanks for calling. How can I help you today?"
            minRows={2}
            {...form.getInputProps("greeting")}
          />

          <Select
            label="AI Voice"
            description="The voice the AI will use when speaking"
            data={VOICE_OPTIONS}
            {...form.getInputProps("voice_id")}
          />
        </Stack>
      </Paper>

      <Paper p="md" withBorder>
        <Text fw={600} mb="md">
          Behavior
        </Text>
        <Stack gap="sm">
          <Textarea
            label="Custom Instructions"
            description="Additional instructions for how the AI should behave (e.g., mention promotions, upsell services)"
            placeholder="Always mention our current promotion: 20% off first visit. Be friendly and casual."
            minRows={3}
            {...form.getInputProps("system_prompt_instructions")}
          />

          <TextInput
            label="Transfer Number"
            description="Phone number to transfer to when a caller asks for a human"
            placeholder="+14155551234"
            {...form.getInputProps("transfer_number")}
          />

          <Divider my="xs" />

          <div>
            <Group justify="space-between">
              <div>
                <Text fw={500} size="sm">
                  Business Hours Forwarding
                </Text>
                <Text size="xs" c="dimmed">
                  When enabled, calls are forwarded to the transfer number
                  during business hours. After hours, the AI will take a message
                  instead. When disabled, the AI always takes a message for
                  non-booking requests.
                </Text>
              </div>
              <Switch
                checked={form.values.use_business_hours_forwarding}
                onChange={(e) =>
                  form.setFieldValue(
                    "use_business_hours_forwarding",
                    e.currentTarget.checked,
                  )
                }
              />
            </Group>
          </div>
        </Stack>
      </Paper>

      <Group justify="flex-end">
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          onClick={handleSave}
          loading={saving}
        >
          Save Settings
        </Button>
      </Group>
    </Stack>
  );
}
